import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "../lib/api";
import { forgetUser, isOfflineError, offlineUser, rememberUser, syncNow } from "../lib/offlineSync";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon

  const loadMe = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      await rememberUser(data.user);
      setUser(data.user);
      syncNow();
    } catch (error) {
      // Server non raggiungibile: continua l'utente già collegato su questo dispositivo (se la sessione è recente).
      const cached = isOfflineError(error) ? await offlineUser() : null;
      setUser(cached || false);
    }
  }, []);

  // Tornata la rete dopo un avvio offline: si verifica la sessione vera sul server.
  useEffect(() => {
    if (!user?.offline) return undefined;
    const back = () => loadMe();
    window.addEventListener("online", back);
    return () => window.removeEventListener("online", back);
  }, [user, loadMe]);

  useEffect(() => {
    // Migrazione dalle versioni precedenti: il JWT non deve restare accessibile a JavaScript.
    localStorage.removeItem("access_token");
    loadMe();
  }, [loadMe]);

  // Con la verifica in due passaggi attiva il server risponde con un "challenge":
  // la sessione nasce solo dopo verifyTwoFactor con il codice dell'app.
  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.two_factor_required) return { twoFactorChallenge: data.challenge };
    await rememberUser(data.user);
    setUser(data.user);
    syncNow();
    return data.user;
  };

  const verifyTwoFactor = async (challenge, code, rememberDevice) => {
    const { data } = await api.post("/auth/2fa/verify", { challenge, code, remember_device: rememberDevice });
    await rememberUser(data.user);
    setUser(data.user);
    syncNow();
    return data;
  };

  const pinLogin = async (payload) => {
    const { data } = await api.post("/auth/pin-login", payload);
    await rememberUser(data.user);
    setUser(data.user);
    syncNow();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    // Le registrazioni non ancora confermate dal server restano sul dispositivo.
    await forgetUser();
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, login, verifyTwoFactor, pinLogin, logout, reload: loadMe }}>
      {children}
    </AuthContext.Provider>
  );
}
